/**
 * 保護されたルート
 * 認証が必要なページへのアクセスを制御
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUser } from '@/hooks/useUser';
import LoadingTips from '@/components/common/LoadingTips';

/**
 * ローディング表示。全画面ローディングは使い方ヒント付きに統一する（#205）。
 */
const LoadingSpinner = () => <LoadingTips className="bg-text-50" />;

/**
 * 保護されたルートコンポーネント
 * 認証済みユーザーのみアクセス可能
 * 未認証の場合はログインページへリダイレクト
 */
export const ProtectedRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isProfileComplete, isLoading: isProfileLoading } = useUser();
  const location = useLocation();

  // 認証状態を確認中
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // 未認証の場合、ログインページへリダイレクト（returnToで元のURLを保持）
  if (!isAuthenticated) {
    const currentUrl = location.pathname + location.search;
    const loginUrl = currentUrl !== '/'
      ? `/auth?returnTo=${encodeURIComponent(currentUrl)}`
      : '/auth';
    return <Navigate to={loginUrl} replace />;
  }

  // 認証済みだがプロフィールが未完了の場合、オンボーディングページへ
  if (isProfileLoading) return <LoadingSpinner />;
  if (!isProfileComplete) {
    // Defer to OnboardingGuard to decide the correct onboarding route
    // (e.g. account-not-found vs complete-profile) to avoid redirect loops.
    return <LoadingSpinner />;
  }

  // 認証済みの場合、子ルートを表示
  return <Outlet />;
};
